import service, { errorReason, errorStatus } from "@/services/publicProfile";
import {
  DEFAULT_PRO_SECTIONS,
  DEFAULT_PRO_TEMPLATES,
  DEFAULT_SECTION_ORDER,
  DEFAULT_THEME,
} from "@/components/public-profile/constants";
import { trackDraftChange, trackPublicProfile } from "@/components/public-profile/tracking";

// Campos que o talento edita no editor e que vao no PUT (EditorState.page).
const DRAFT_FIELDS = [
  "template",
  "theme",
  "sections",
  "hidden_items",
  "headline",
  "tagline",
  "search_indexing",
  "hide_branding",
  "hide_resume_download",
  "hide_share",
  "show_experience_details",
  "character",
  "game_only",
  "platformer_scene",
  "presentation_backdrop",
  "presentation_pace",
];

const SAVE_DEBOUNCE_MS = 800;

// Fora do state: timer e promessa nao sao serializaveis (o store e persistido).
let saveTimer = null;
// true so no "Publicar com este modelo": o proximo save leva o modelo novo para o ar.
let forceTemplate = false;
let savePromise = null;

function normalizeSections(sections, proSections) {
  const list = Array.isArray(sections) ? sections.filter((s) => s && s.id) : [];
  const known = list.map((s) => s.id);
  DEFAULT_SECTION_ORDER.forEach((id) => {
    if (!known.includes(id)) {
      list.push({ id, visible: !proSections.includes(id) });
    }
  });
  return list.map((s) => ({ id: s.id, visible: !!s.visible }));
}

function draftFromPage(page, pro) {
  const proSections = (pro && pro.sections) || DEFAULT_PRO_SECTIONS;
  const hidden = (page && page.hidden_items) || {};
  return {
    template: (page && page.template) || "classic",
    theme: { ...DEFAULT_THEME, ...((page && page.theme) || {}) },
    sections: normalizeSections(page && page.sections, proSections),
    hidden_items: {
      experiences: Array.isArray(hidden.experiences) ? [...hidden.experiences] : [],
      education: Array.isArray(hidden.education) ? [...hidden.education] : [],
    },
    headline: (page && page.headline) || "",
    tagline: (page && page.tagline) || "",
    search_indexing: !!(page && page.search_indexing),
    hide_branding: !!(page && page.hide_branding),
    hide_resume_download: !!(page && page.hide_resume_download),
    hide_share: !!(page && page.hide_share),
    show_experience_details: !!(page && page.show_experience_details),
    character: (page && page.character) || null,
    game_only: !!(page && page.game_only),
    platformer_scene: (page && page.platformer_scene) || null,
    presentation_backdrop: (page && page.presentation_backdrop) || null,
    presentation_pace: (page && page.presentation_pace) || null,
  };
}

const PRO_FLAGS = ["hide_branding", "hide_resume_download", "hide_share"];

// Modo demonstracao: quem e gratis testa tudo do PRO na previa, mas o que e PRO
// nao vai para o servidor (o perfil no ar nao muda). Troca o PRO pelo que ja
// estava salvo e diz se trocou alguma coisa.
function stripProForFree(payload, editor) {
  const saved = (editor && editor.page) || {};
  const proTemplates = (editor && editor.pro && editor.pro.templates) || DEFAULT_PRO_TEMPLATES;
  const proSections = (editor && editor.pro && editor.pro.sections) || DEFAULT_PRO_SECTIONS;
  let stripped = false;

  if (proTemplates.includes(payload.template)) {
    payload.template = proTemplates.includes(saved.template) ? "classic" : saved.template || "classic";
    stripped = true;
  }
  const savedSections = Array.isArray(saved.sections) ? saved.sections : [];
  payload.sections = (payload.sections || []).map((section) => {
    if (!proSections.includes(section.id) || !section.visible) return section;
    const before = savedSections.find((item) => item.id === section.id);
    const visible = !!(before && before.visible);
    if (visible !== section.visible) stripped = true;
    return { ...section, visible };
  });
  PRO_FLAGS.forEach((flag) => {
    if (payload[flag]) {
      payload[flag] = !!saved[flag];
      stripped = true;
    }
  });
  return stripped;
}

// Dono do rascunho: o public_id e unico por talento. Serve para nao levar o
// rascunho de uma conta para outra quando o store sobrevive a troca de login.
function ownerOf(editor) {
  return (editor && editor.page && editor.page.public_id) || null;
}

// Perfil no ar com outro modelo so na previa (ate "Publicar com este modelo").
function isTemplatePending(s) {
  const page = s.editor && s.editor.page;
  return !!(page && page.is_published && s.draft && s.draft.template !== page.template);
}

function payloadFromDraft(draft) {
  const payload = {};
  DRAFT_FIELDS.forEach((field) => {
    payload[field] = draft[field];
  });
  payload.headline = draft.headline ? draft.headline.trim() || null : null;
  payload.tagline = draft.tagline ? draft.tagline.trim() || null : null;
  return payload;
}

const state = () => ({
  demo: false, // rascunho tem coisa PRO que nao foi salva (modo demonstracao)
  // Perfil no ar: trocar de modelo e so previa ate "Publicar com este modelo".
  templatePending: false,
  editor: null, // EditorState da API
  owner: null, // public_id do perfil a que o rascunho pertence
  draft: null, // copia editavel de editor.page
  loading: false,
  loaded: false,
  loadError: false,
  dirty: false,
  revision: 0,
  savedVersion: 0, // sobe a cada PUT salvo; a previa usa para recarregar
  saveStatus: "idle", // idle | pending | saving | saved | error
});

const getters = {
  page: (s) => (s.editor && s.editor.page) || null,
  plan: (s) => (s.editor && s.editor.plan) || { is_pro: false, type: null },
  isPro: (s, g) => !!g.plan.is_pro,
  prices: (s) =>
    (s.editor && s.editor.prices) || {
      monthly_cents: 990,
      lifetime_cents: 6990,
      currency: "brl",
    },
  proTemplates: (s) =>
    (s.editor && s.editor.pro && s.editor.pro.templates) || DEFAULT_PRO_TEMPLATES,
  proSections: (s) =>
    (s.editor && s.editor.pro && s.editor.pro.sections) || DEFAULT_PRO_SECTIONS,
  items: (s) =>
    (s.editor && s.editor.items) || { experiences: [], education: [] },
  counts: (s) => (s.editor && s.editor.counts) || {},
  isPublished: (s, g) => !!(g.page && g.page.is_published),
  hasEverPublished: (s, g) => !!(g.page && (g.page.is_published || g.page.published_at)),
  // Recursos PRO que o rascunho usa (modelo, secoes ligadas e rodape sem o selo).
  proFeaturesInUse: (s, g) => {
    if (!s.draft) return [];
    const used = [];
    if (g.proTemplates.includes(s.draft.template)) used.push(s.draft.template);
    s.draft.sections.forEach((section) => {
      if (section.visible && g.proSections.includes(section.id)) used.push(section.id);
    });
    ["hide_branding", "hide_resume_download", "hide_share"].forEach((flag) => {
      if (s.draft[flag]) used.push(flag);
    });
    return used;
  },
  needsPro: (s, g) => !g.isPro && g.proFeaturesInUse.length > 0,
};

const mutations = {
  SET_LOADING(s, value) {
    s.loading = value;
  },
  SET_LOAD_ERROR(s, value) {
    s.loadError = value;
  },
  SET_EDITOR(s, editor) {
    s.editor = editor;
    s.owner = ownerOf(editor);
    s.loaded = true;
    s.loadError = false;
  },
  RESET_DRAFT(s) {
    s.draft = s.editor ? draftFromPage(s.editor.page, s.editor.pro) : null;
    s.dirty = false;
  },
  PATCH_DRAFT(s, patch) {
    s.draft = { ...(s.draft || {}), ...patch };
    s.dirty = true;
    s.revision += 1;
    s.saveStatus = "pending";
  },
  SET_DIRTY(s, value) {
    s.dirty = value;
  },
  BUMP_SAVED_VERSION(s) {
    s.savedVersion += 1;
  },
  SET_SAVE_STATUS(s, value) {
    s.saveStatus = value;
  },
  SET_DEMO(s, value) {
    s.demo = value;
  },
  SET_TEMPLATE_PENDING(s, value) {
    s.templatePending = value;
  },
  CLEAR(s) {
    s.demo = false;
    s.templatePending = false;
    s.editor = null;
    s.owner = null;
    s.draft = null;
    s.loaded = false;
    s.loadError = false;
    s.dirty = false;
    s.revision = 0;
    s.saveStatus = "idle";
  },
};

const actions = {
  async fetch({ commit, state: s }, { keepDraft = false } = {}) {
    commit("SET_LOADING", true);
    try {
      const editor = await service.show();
      // Rascunho de outra conta (trocou de login na mesma aba): nunca aproveita.
      const sameOwner = !!s.draft && s.owner === ownerOf(editor);
      commit("SET_EDITOR", editor);
      if (!keepDraft || !sameOwner || !(s.dirty || s.demo)) {
        clearTimeout(saveTimer);
        commit("RESET_DRAFT");
        commit("SET_DEMO", false);
      }
      commit("SET_TEMPLATE_PENDING", isTemplatePending(s));
      if (s.saveStatus === "saving" || s.saveStatus === "pending") {
        commit("SET_SAVE_STATUS", "idle");
      }
      return editor;
    } catch (error) {
      commit("SET_LOAD_ERROR", true);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Altera o rascunho e agenda o salvamento automatico.
  // `_track` (opcional) diz de onde veio a mudanca, so para o analytics: nao vai para o rascunho.
  edit({ state: s, commit, dispatch }, payload) {
    const { _track: meta, ...patch } = payload || {};
    const before = s.draft;
    commit("PATCH_DRAFT", patch);
    trackDraftChange(() => s, before, patch, meta || {});
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      dispatch("save").catch(() => {});
    }, SAVE_DEBOUNCE_MS);
  },

  async save({ state: s, commit, dispatch, getters }) {
    clearTimeout(saveTimer);
    // Um PUT por vez: quem chega enquanto outro salva espera e, se ainda
    // houver o que salvar, manda o proximo (nunca dois ao mesmo tempo).
    while (savePromise) {
      await savePromise.catch(() => {});
    }
    if (!s.dirty || !s.draft) return s.editor;

    const revision = s.revision;
    commit("SET_SAVE_STATUS", "saving");
    const payload = payloadFromDraft(s.draft);
    commit("SET_DEMO", !getters.isPro && stripProForFree(payload, s.editor));
    // Perfil no ar: o resto salva sozinho, mas o modelo so muda quando a pessoa confirma.
    const live = s.editor && s.editor.page;
    if (live && live.is_published && !forceTemplate) payload.template = live.template;
    const request = service.update(payload);
    savePromise = request;
    try {
      const editor = await request;
      commit("SET_EDITOR", editor);
      commit("SET_TEMPLATE_PENDING", isTemplatePending(s));
      commit("BUMP_SAVED_VERSION");
      if (s.revision === revision) {
        commit("SET_DIRTY", false);
        commit("SET_SAVE_STATUS", "saved");
      } else {
        // Mudou algo enquanto salvava: salva de novo com o rascunho novo.
        commit("SET_SAVE_STATUS", "pending");
        saveTimer = setTimeout(() => {
          dispatch("save").catch(() => {});
        }, SAVE_DEBOUNCE_MS);
      }
      return editor;
    } catch (error) {
      commit("SET_SAVE_STATUS", "error");
      trackPublicProfile(s, "public_profile_save_failed", {
        status: errorStatus(error),
        reason: errorReason(error) || (error && error.message) || null,
      });
      throw error;
    } finally {
      if (savePromise === request) savePromise = null;
    }
  },

  // "Publicar com este modelo": leva para o ar o modelo que esta so na previa.
  // Espera o save em andamento antes: ele limparia o dirty e este nao salvaria nada.
  async applyTemplate({ dispatch }) {
    await dispatch("flush");
    return dispatch("saveForcingTemplate");
  },

  // Virou PRO (voltou do pagamento): o que estava so na demonstracao passa a valer,
  // inclusive o modelo (foi para usar ele que a pessoa pagou).
  async applyDemo({ state: s, dispatch }) {
    if (!(s.demo || s.dirty) || !s.draft) return s.editor;
    await dispatch("flush");
    return dispatch("saveForcingTemplate");
  },

  async saveForcingTemplate({ commit, dispatch }) {
    forceTemplate = true;
    commit("SET_DIRTY", true);
    try {
      return await dispatch("save");
    } finally {
      forceTemplate = false;
    }
  },

  // Salva ja o que estiver pendente (antes de publicar, sair, abrir o PDF).
  async flush({ state: s, dispatch }) {
    clearTimeout(saveTimer);
    if (s.dirty) return dispatch("save");
    if (savePromise) await savePromise.catch(() => {});
    return s.editor;
  },

  async publish({ state: s, commit, dispatch }) {
    await dispatch("flush");
    // Modelo que estava so na previa vai junto para o ar.
    if (s.templatePending) await dispatch("saveForcingTemplate");
    const editor = await service.publish();
    commit("SET_EDITOR", editor);
    commit("SET_TEMPLATE_PENDING", isTemplatePending(s));
    return editor;
  },

  async unpublish({ state: s, commit, dispatch }) {
    await dispatch("flush");
    const editor = await service.unpublish();
    commit("SET_EDITOR", editor);
    commit("SET_TEMPLATE_PENDING", isTemplatePending(s));
    return editor;
  },

  async changeSlug({ commit }, slug) {
    const editor = await service.changeSlug(slug);
    commit("SET_EDITOR", editor);
    return editor;
  },

  // Exclui de vez. O rascunho some junto: nada pode salvar depois e recriar o perfil sozinho.
  async destroy({ commit, dispatch }) {
    // Salvamento em andamento terminaria depois e criaria um perfil novo.
    await dispatch("flush").catch(() => {});
    clearTimeout(saveTimer);
    await service.destroy();
    commit("CLEAR");
  },

  clear({ commit }) {
    clearTimeout(saveTimer);
    commit("CLEAR");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
